<script lang="ts">
  import z, { ZodError } from 'zod';

  import type { FormFieldError } from '$lib/utils/definitions';
  import { formatFormErrors } from '$lib/utils/helpers';
  import { PretLisse, type Logement, type Zone } from '$lib/utils/lissage-ptz';

  import Wrapper from '$components/common/Simulator/Wrapper.svelte';
  import Description from '$components/common/Simulator/Description.svelte';
  import Form from '$components/common/Simulator/Form.svelte';
  import Input from '$components/common/Input.svelte';
  import Actions from '$components/common/Simulator/Actions.svelte';
  import Action from '$components/common/Simulator/Action.svelte';
  import { formatEuro } from '$lib/utils/formatters';
  import Badge from '$components/common/Badge.svelte';
  import RadioFieldset from '$components/common/RadioFieldset.svelte';
  import Radio from '$components/common/Radio.svelte';

  import acquisitionSimulatorManager from '$lib/managers/acquisition-simulator.svelte';

  let {
    currentStep,
    steps,
    totalCost,
    interestRate,
    loanDuration,
    loanAmount,
    housingType,
    inHousePeopleAmount,
    fiscalIncome,
    ptzType,
    brsZone,
    ownContribution,
    loading,
    acquisitionSimulation,
    previousStep,
    nextStep,
    updateAcquisitionSimulation,
    goToPreviousStep,
  } = $derived(acquisitionSimulatorManager);

  let errors: FormFieldError = $state({});

  const FormData = $derived(
    z.object({
      interestRate: z
        .number({
          message: 'Veuillez remplir ce champs.',
        })
        .positive('Veuillez saisir un chiffre supérieur à 0.'),
      loanDuration: z
        .number({
          message: 'Veuillez remplir ce champs.',
        })
        .gte(10, 'La durée du prêt ne peut pas être inférieure à 10 ans.')
        .lte(25, 'La durée du prêt ne peut pas être supérieure à 25 ans.'),
      ...(housingType === 'new' && {
        inHousePeopleAmount: z
          .number({
            message: 'Veuillez remplir ce champs.',
          })
          .positive('Veuillez saisir un chiffre supérieur à 0.'),
        fiscalIncome: z
          .number({
            message: 'Veuillez remplir ce champs.',
          })
          .positive('Veuillez saisir un chiffre supérieur à 0.'),
        ptzType: z.string({
          message: 'Veuillez selectionner le type du logement.',
        }),
      }),
    }),
  );

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();

    const payload = {
      interestRate,
      loanDuration,
      ...(housingType === 'new' && {
        inHousePeopleAmount,
        fiscalIncome,
        ptzType,
      }),
    };

    try {
      FormData.parse(payload);
      errors = {};

      acquisitionSimulatorManager.pretLisse = new PretLisse(
        totalCost,
        brsZone as Zone,
        ownContribution || 0,
        interestRate as number,
        loanDuration as number,
        inHousePeopleAmount as number,
        fiscalIncome as number,
        ptzType as Logement,
      );

      if (acquisitionSimulation) {
        await updateAcquisitionSimulation(payload);
      }
    } catch (e) {
      errors = formatFormErrors((e as ZodError).issues);
    }
  };
</script>

<Wrapper>
  <Description
    title="Simulateur d'acquisition"
    stepTitle={`${currentStep.step}. ${currentStep.title}`}
    nextStepTitle={nextStep?.title}
    currentStep={currentStep.step}
    stepCount={steps.length}>
    <p>
      Renseignez les différentes informations relatives à un prêt immobilier
      pour simuler votre projet d'acquisition. Selon les critères que vous aurez
      rempli, nous allons pouvoir estimer la nature du financement de votre
      projet en bail réel solidaire (BRS), avec un prêt immobilier classique et
      éventuellement un prêt à taux zéro (PTZ) si vous êtes éligible.
    </p>
  </Description>

  <Form onSubmit={handleSubmit}>
    <fieldset class="fr-fieldset">
      <div class="fr-fieldset__element">
        <p class="fr-h6"><u>Paramètres du prêt immobilier</u></p>
      </div>

      <div class="fr-fieldset__element fr-mb-4w">
        <div class="fr-input-group">
          <p class="fr-label fr-mb-0"><b>Montant à emprunter (€)</b></p>
          <p class="fr-hint-text fr-mb-1w">
            Le montant à emprunter est pré-calculé en fonction de vos réponses
            précédentes.
          </p>
          <Badge status="success">
            {formatEuro(loanAmount as number)}
          </Badge>
        </div>
      </div>

      <div class="fr-fieldset__element fr-mb-4w">
        <Input
          value={interestRate}
          label="Taux d'intérêt de votre crédit (%) *"
          labelTooltip="Renseigner le taux d'intérêt hors assurance."
          type="number"
          id="interest-rate"
          step={0.01}
          placeholder="Exemple: 3.25"
          error={errors.interestRate}
          onChange={(e) => {
            acquisitionSimulatorManager.interestRate = Number(
              (e.target as HTMLInputElement).value,
            );
          }} />
      </div>

      <div class="fr-fieldset__element fr-mb-4w">
        <Input
          value={loanDuration}
          label="Durée de remboursement (années) *"
          labelTooltip="La durée de remboursement est en moyenne de 20 à 25 ans."
          type="number"
          id="loan-duration"
          step={1}
          placeholder="Exemple: 25"
          error={errors.loanDuration}
          onChange={(e) => {
            acquisitionSimulatorManager.loanDuration = Number(
              (e.target as HTMLInputElement).value,
            );
          }} />
      </div>
    </fieldset>

    {#if housingType !== 'new'}
      <fieldset class="fr-fieldset">
        <div class="fr-fieldset__element">
          <p class="fr-h6"><u>Paramètres du prêt à taux zéro (PTZ)</u></p>
        </div>
        <div class="fr-fieldset__element">
          <p>
            Notre simulateur ne propose pas encore de simulation de prêt à taux
            zéro pour les biens anciens.
          </p>
        </div>
      </fieldset>
    {:else}
      <fieldset class="fr-fieldset">
        <div class="fr-fieldset__element fr-mb-4w">
          <Input
            value={inHousePeopleAmount}
            label="Nombre de personnes dans le foyer *"
            type="number"
            id="in-house-people-amount"
            step={1}
            placeholder="Exemple: 5"
            error={errors.inHousePeopleAmount}
            onChange={(e) => {
              acquisitionSimulatorManager.inHousePeopleAmount = Number(
                (e.target as HTMLInputElement).value,
              );
            }} />
        </div>

        <div class="fr-fieldset__element">
          <Input
            value={fiscalIncome}
            label="Revenu fiscal de référence N-2 (€) *"
            labelTooltip={`Le revenu fiscal de référence est la somme des revenus fiscaux de référence de tous les futurs occupants, basée sur l'année n-2.
                <br/> Exemple : Pour un prêt en 2025, on prend les revenus fiscaux de référence de 2023.`}
            type="number"
            id="fiscal-income"
            step={1}
            placeholder="Exemple: 25 000€"
            error={errors.fiscalIncome}
            onChange={(e) => {
              acquisitionSimulatorManager.fiscalIncome = Number(
                (e.target as HTMLInputElement).value,
              );
            }} />
        </div>
      </fieldset>

      <RadioFieldset
        legend="Type du logement *"
        legendTooltip={`
            <div class="fr-p-2w">
              Un logement collectif fait partie d'une copropriété, alors que un logement individuel ne fait partie d'aucune copropriété.
            </div>
          `}
        error={errors.ptzType}>
        <Radio
          label="Collectif"
          checked={ptzType === 'collectif'}
          oninput={() => (acquisitionSimulatorManager.ptzType = 'collectif')} />
        <Radio
          label="Individuel"
          checked={ptzType === 'individuel'}
          oninput={() =>
            (acquisitionSimulatorManager.ptzType = 'individuel')} />
      </RadioFieldset>
    {/if}

    <Actions>
      <Action
        direction="previous"
        label={previousStep?.title as string}
        onClick={goToPreviousStep}
        {loading} />
      <Action
        direction="next"
        label={nextStep?.title as string}
        type="submit"
        {loading} />
    </Actions>
  </Form>
</Wrapper>
